/**
 * Which menu buttons sit in the row, and in what order.
 *
 * The arrangement is the user's own and is kept on them. It used to be learnt from what they
 * opened most, which meant the row rearranged itself between sessions and nobody could say why a
 * button had moved.
 */

/**
 * Which arrangement is in force: the one chosen since the page loaded, or the stored one.
 *
 * The stored one arrives with /me a moment after the first render, so it cannot be read into
 * state once and kept — an initial value taken from it is always the empty one, and the row then
 * fell back to the primary buttons after every reload however often it had been saved.
 *
 * `??` and not `||`: an empty row is an arrangement someone chose, and falling back out of it
 * would put the default buttons back the moment they emptied it.
 */
const arrangementInForce = (chosen, stored) => chosen ?? stored

/** A button that cannot be pinned — the ones that are not windows, like logging out. */
const canPin = (entry) => entry?.promotable !== false

/**
 * The entries to draw in the row, in the user's order.
 *
 * `stored` is what they chose, which is not the same as what they can see: a permission can come
 * and go, and losing one for a week should not quietly lose the arrangement with it. So the stored
 * order is kept whole and filtered down to what is on offer, rather than being rewritten.
 *
 * An arrangement they have never made (`undefined`, as opposed to an empty row they chose) falls
 * back to the buttons marked primary, so someone who never touches this gets the row as it was.
 */
const pinnedEntries = (stored, entries) => {
  const keys =
    stored ?? entries.filter((entry) => entry.primary).map((entry) => entry.key)
  const byKey = new Map(entries.map((entry) => [entry.key, entry]))
  return keys
    .map((key) => byKey.get(key))
    .filter((entry) => entry && canPin(entry))
}

/** Pins a button that is not in the row, or takes out one that is. A new one goes on the end. */
const toggled = (keys, key) =>
  keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key]

/**
 * Moves the button at `from` to where the one at `to` sits, pushing the rest along — what dropping
 * a button onto another one should do.
 */
const movedTo = (keys, from, to) => {
  if (from === to || !keys[from] || to < 0 || to >= keys.length) return keys
  const next = [...keys]
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)
  return next
}

export { canPin, arrangementInForce, pinnedEntries, toggled, movedTo }
